import classNames from 'classnames';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import variables from '@/components/modules/destination-facts/destination-facts.variables';
import BarChart, {
  BarChartColors,
} from '@/components/molecules/bar-chart/bar-chart';
import CountUp from '@/components/molecules/count-up/count-up';
import StarRating from '@/components/molecules/star-rating/star-rating';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Link from '@/components/utilities/link/link';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { formatNumber } from '@/libraries/formatters/format-number';
import getLocale from '@/libraries/getters/get-locale';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import DestinationFactsTable from './destination-facts-table/destination-facts-table';
import styles from './destination-facts.module.scss';

export interface DestinationFactsProps extends ModuleWrapperProps {
  /** Destination information to display */
  destinationInformation?: Content.Properties.DestinationInformation;
  /** The text to show as a headline */
  headline?: string;
}

const DestinationFacts = ({
  destinationInformation,
  headline,
  ...rest
}: DestinationFactsProps) => {
  const { t } = useI18n();

  const isTablet = useWindowSize('tablet');

  const renderOverviewBlock = ({
    heading,
    length,
    unit,
  }: {
    heading: string;
    length: number;
    unit: string;
  }) => {
    const locale = getLocale();

    return (
      <div className={styles.overviewBlock}>
        <div className={styles.heading}>
          <Typography tagStyle="labelMedium">{heading}</Typography>
        </div>

        <div className={styles.length}>
          <Typography className={styles.amount} tagStyle="displayLarge">
            <CountUp
              formatter={length => formatNumber({ locale, value: length })}
              value={length}
            />
          </Typography>

          <div className={styles.unit}>
            <Typography tagStyle="displayMedium">{unit}</Typography>
          </div>
        </div>
      </div>
    );
  };

  const createValueRow = ({
    color,
    label,
    showBar = false,
    totalValue,
    unit,
    value,
  }: {
    color?: string;
    label: string;
    showBar?: boolean;
    totalValue?: number;
    unit?: string;
    value: number;
  }) => {
    const locale = getLocale();

    return (
      <div
        className={classNames(styles.valueRow, {
          [styles.withThreeColumns]: showBar,
        })}
      >
        <div className={styles.valueRowItem}>
          <Typography tagStyle="bodyMedium">{label}</Typography>
        </div>

        {showBar && totalValue && isTablet && (
          <div className={classNames(styles.valueRowItem, styles.bar)}>
            <BarChart
              value={(value / totalValue) * 100}
              variant={color as BarChartColors}
            />
          </div>
        )}

        <div className={classNames(styles.valueRowItem, styles.value)}>
          <Typography tagStyle="bodyMediumBold">
            {formatNumber({ locale, value })} {unit}
          </Typography>
        </div>
      </div>
    );
  };

  const createRatingRow = (label: string, value: number | null) => {
    if (value === null) return null;

    return (
      <div className={styles.valueRow}>
        <Typography>{label}</Typography>

        <StarRating className={styles.value} rating={value}></StarRating>
      </div>
    );
  };

  return (
    <ModuleWrapper
      {...rest}
      className={styles.component}
      settings={{
        anchorLinkSettings: rest.settings?.anchorLinkSettings ?? null,
        brand: rest.settings?.brand ?? '',
        showBottomIllustrations:
          rest.settings?.showBottomIllustrations ?? false,
        showTopIllustrations: rest.settings?.showTopIllustrations ?? false,
        showTransparentBackground:
          rest.settings?.showTransparentBackground ?? false,
        theme: variables.settings.theme ?? null,
      }}
    >
      <Container className={styles.container}>
        {headline && (
          <Row>
            <Column className={styles.headlineColumn}>
              {headline && (
                <Typography
                  className={styles.headline}
                  tag="h2"
                  tagStyle="displayMedium"
                >
                  {headline}
                </Typography>
              )}
            </Column>
          </Row>
        )}

        {destinationInformation?.destinationOverview && (
          <Row className={styles.destinationOverview}>
            <Column width={{ desktop: 4, tablet: 4 }}>
              {renderOverviewBlock({
                heading: t('items.modules.destinationFacts.totalTrailLength'),
                length:
                  destinationInformation?.destinationOverview
                    .totalTrailLengthKilometers,
                unit: 'km',
              })}
            </Column>

            <Column width={{ desktop: 4, tablet: 4 }}>
              {renderOverviewBlock({
                heading: t(
                  'items.modules.destinationFacts.baseElevationLength',
                ),
                length:
                  destinationInformation?.destinationOverview
                    .baseElevationLengthMeters,
                unit: 'm',
              })}
            </Column>

            <Column width={{ desktop: 4, tablet: 4 }}>
              {renderOverviewBlock({
                heading: t('items.modules.destinationFacts.topElevation'),
                length:
                  destinationInformation?.destinationOverview
                    .topElevationMeters,
                unit: 'm',
              })}
            </Column>
          </Row>
        )}

        {destinationInformation?.destinationTrails &&
          destinationInformation?.destinationLifts && (
            <Row className={styles.tablesRow}>
              <Column width={{ laptop: 6 }}>
                <DestinationFactsTable
                  headline={t('items.modules.destinationFacts.pistes')}
                >
                  {createValueRow({
                    color: 'blue',
                    label: t('items.modules.destinationFacts.blueTrail'),
                    showBar: true,
                    totalValue:
                      destinationInformation?.destinationOverview
                        .totalTrailLengthKilometers,
                    unit: 'km',
                    value:
                      destinationInformation?.destinationTrails.blueTrailCount,
                  })}

                  {createValueRow({
                    color: 'red',
                    label: t('items.modules.destinationFacts.redTrail'),
                    showBar: true,
                    totalValue:
                      destinationInformation?.destinationOverview
                        .totalTrailLengthKilometers,
                    unit: 'km',
                    value:
                      destinationInformation?.destinationTrails.redTrailCount,
                  })}

                  {createValueRow({
                    color: 'black',
                    label: t('items.modules.destinationFacts.blackTrail'),
                    showBar: true,
                    totalValue:
                      destinationInformation?.destinationOverview
                        .totalTrailLengthKilometers,
                    unit: 'km',
                    value:
                      destinationInformation?.destinationTrails.blackTrailCount,
                  })}

                  <div
                    className={classNames(
                      styles.valueRow,
                      styles.withThreeColumns,
                    )}
                  >
                    <Typography>
                      {t('items.modules.destinationFacts.offTrail')}
                    </Typography>

                    {/* needed for spacing in the table */}
                    {isTablet && <span></span>}

                    <div className={styles.value}>
                      <Icon
                        name={
                          destinationInformation?.destinationTrails.offPiste
                            ? 'CheckMark'
                            : 'Close'
                        }
                      />
                    </div>
                  </div>
                </DestinationFactsTable>
              </Column>

              <Column width={{ laptop: 6 }}>
                <DestinationFactsTable
                  headline={destinationInformation?.destinationName}
                >
                  {createValueRow({
                    label: t('items.modules.destinationFacts.liftCount'),
                    value: destinationInformation?.destinationLifts.liftCount,
                  })}

                  {createValueRow({
                    label: t('items.modules.destinationFacts.liftCapacity'),
                    unit: t('items.common.unitOfMeasure.personsPerHour'),
                    value:
                      destinationInformation?.destinationLifts
                        .liftCapacitySkierPerHour,
                  })}

                  {createValueRow({
                    label: t('items.modules.destinationFacts.longestRun'),
                    unit: 'km',
                    value:
                      destinationInformation?.destinationLifts
                        .longestRunKilometers,
                  })}

                  <div className={styles.valueRow}>
                    <Typography>
                      {t('items.modules.destinationFacts.funPark')}
                    </Typography>

                    <div className={styles.value}>
                      <Icon
                        name={
                          destinationInformation?.destinationLifts.funPark
                            ? 'CheckMark'
                            : 'Close'
                        }
                      />
                    </div>
                  </div>
                </DestinationFactsTable>
              </Column>
            </Row>
          )}

        <Row>
          <Column className={styles.multiTablesColumn}>
            {destinationInformation?.destinationRatings.skiingRatings && (
              <DestinationFactsTable
                className={styles.tile}
                headline={t('items.modules.destinationFacts.skiing')}
              >
                {createRatingRow(
                  t('items.modules.destinationFacts.childrenRating'),
                  destinationInformation.destinationRatings.skiingRatings
                    .childrenRating,
                )}

                {createRatingRow(
                  t('items.modules.destinationFacts.beginnerRating'),
                  destinationInformation.destinationRatings.skiingRatings
                    .beginnersRating,
                )}

                {createRatingRow(
                  t('items.modules.destinationFacts.intermediateRating'),

                  destinationInformation.destinationRatings.skiingRatings
                    .intermediateRating,
                )}

                {createRatingRow(
                  t('items.modules.destinationFacts.expertRating'),
                  destinationInformation.destinationRatings.skiingRatings
                    .expertRating,
                )}

                {createRatingRow(
                  t('items.modules.destinationFacts.offTrailRating'),
                  destinationInformation.destinationRatings.skiingRatings
                    .offPisteRating,
                )}

                {createRatingRow(
                  t('items.modules.destinationFacts.snowboardRating'),
                  destinationInformation.destinationRatings.skiingRatings
                    .snowboardRating,
                )}
              </DestinationFactsTable>
            )}

            {destinationInformation?.destinationRatings.locationRatings && (
              <DestinationFactsTable
                className={styles.tile}
                headline={t('items.modules.destinationFacts.city')}
              >
                {createRatingRow(
                  t('items.modules.destinationFacts.charmRating'),

                  destinationInformation.destinationRatings.locationRatings
                    .charmRating,
                )}

                {createRatingRow(
                  t('items.modules.destinationFacts.shoppingRating'),
                  destinationInformation.destinationRatings.locationRatings
                    .shoppingRating,
                )}

                {createRatingRow(
                  t('items.modules.destinationFacts.afterSkiRating'),
                  destinationInformation.destinationRatings.locationRatings
                    .afterSkiRating,
                )}

                {createRatingRow(
                  t('items.modules.destinationFacts.diningRating'),
                  destinationInformation.destinationRatings.locationRatings
                    .diningRating,
                )}

                {createRatingRow(
                  t('items.modules.destinationFacts.diningVillageRating'),
                  destinationInformation.destinationRatings.locationRatings
                    .diningVillageRating,
                )}
              </DestinationFactsTable>
            )}

            {destinationInformation?.webcams && (
              <DestinationFactsTable
                className={styles.tile}
                headline={t('items.modules.destinationFacts.webcam')}
              >
                {destinationInformation?.webcams.map((webcam, webcamIndex) => (
                  <div
                    key={webcamIndex}
                    className={classNames(
                      styles.valueRow,
                      styles.withOneColumn,
                    )}
                  >
                    <Link
                      className={styles.link}
                      target="_blank"
                      url={webcam.url}
                    >
                      {webcam.title}
                    </Link>
                  </div>
                ))}
              </DestinationFactsTable>
            )}
          </Column>
        </Row>
      </Container>
    </ModuleWrapper>
  );
};

DestinationFacts.displayName = 'DestinationFacts';

export default DestinationFacts;
